import { USE_WEBGPU } from '../../../../globals';
import { OutOfCoreTileManager } from '../../OutOfCoreTileManager';
import { OutOfCoreTaskBase } from '../OutOfCoreTaskBase';

/** @import { FragmentList } from "../../../FragmentList" */

/**
 * Base class for geometry upload tasks
 */
export class UploadTaskBase extends OutOfCoreTaskBase {

  /** @type {Map<LeanBufferGeometry, number>} */
  static #geomRefCounts = new Map();

  /** @type {number|undefined} */#memoryCost;

  /**
   * Creates a new task to upload meshes to the GPU
   *
   * @param {OutOfCoreTileManager} outOfCoreTileManager - The OutOfCoreTileManager instance
   * @param {FragmentList} [fragList] - The fragment list. Only needed when geometryIDs are passed to geomsOrGeomIds.
   * @param {number} bvhNodeId - The BVH node ID
   * @param {THREE.BufferGeometry[]|Uint32Array} geomsOrGeomIds - Array of geometries or geometry IDs to upload
   */
  constructor(outOfCoreTileManager, fragList, bvhNodeId, geomsOrGeomIds) {
    super(outOfCoreTileManager);
    this.fragList = fragList;
    this.bvhNodeId = bvhNodeId;
    this.geomsOrGeomIds = geomsOrGeomIds;
  }

  #isReady() {
    if (USE_WEBGPU) {
      return true;
    }

    const model = this.outOfCoreTileManager.model;
    const consolidation = model.getConsolidation(this.bvhNodeId);
    return Boolean(consolidation);
  }

  /**
   * Returns the geometries to be uploaded
   * @returns {THREE.BufferGeometry[]} The geometries to be uploaded
   */
  getGeometries() {
    if (this.geomsOrGeomIds[0] instanceof THREE.BufferGeometry) {
      return this.geomsOrGeomIds;
    }

    return Array.from(
      this.geomsOrGeomIds,
      (geomId) => this.fragList.geoms.getGeometry(geomId)
    );
  }

  /**
   * Uploads the geometries to the GPU
   *
   * @returns {number} The resource cost of the uploaded geometry.
   */
  execute() {
    if (!this.#isReady()) {
      return 0;
    }

    let renderer = this.outOfCoreTileManager.getRenderer();
    let geometries = this.getGeometries();

    let memoryCost = 0;
    for (let geom of geometries) {
      let currentRefCount = UploadTaskBase.#geomRefCounts.get(geom) ?? 0;

      // Uploaded the mesh to the GPU
      if (geom.streamingDraw === true) {
        geom.streamingDraw = false;
        geom.streamingIndex = false;

        renderer.uploadGeometry(geom);

        memoryCost += geom.getByteSize();
      }

      UploadTaskBase.#geomRefCounts.set(geom, currentRefCount + 1);
    }

    // Update the accurate amount of memory consumed by this execution
    this.setResourceAmountConsumedByLastExecution(memoryCost);
  }

  /**
   * Frees memory associated with the instanced geometry.
   */
  undo() {

    let geometries = this.getGeometries();
    let freedMemory = 0;

    for (let geom of geometries) {
      let currentRefCount = (UploadTaskBase.#geomRefCounts.get(geom) ?? 0) - 1;
      if (currentRefCount === 0) {
        UploadTaskBase.#geomRefCounts.delete(geom);

        if (geom.streamingDraw !== true) {
          geom.dispose();
          geom.streamingDraw = true;
          geom.streamingIndex = true;
          freedMemory += geom.getByteSize();
        }
      } else {
        UploadTaskBase.#geomRefCounts.set(geom, currentRefCount);
      }
    }

    this.setResourceAmountFreedByLastExecution(freedMemory);
  }

  /**
   * Get the resource cost for uploading the remaining fragments of the mesh
   *
   * @returns {number} The resource cost in bytes.
   */
  getResourceCost() {
    if (this.#memoryCost === undefined) {
      if (!this.#isReady()) {
        return 0;
      }

      let geometries = this.getGeometries();

      this.#memoryCost = 0;
      for (let geom of geometries) {
        this.#memoryCost += geom.getByteSize();
      }
    }

    return this.#memoryCost;
  }
}